import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { apiFetch as fetch } from "apiClient";

const ScreenerResultsDisplayContext = createContext(null);

export const ScreenerResultsDisplayProvider = ({ children }) => {
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // date filters
    const [dateMode, setDateMode] = useState("single"); // "single" | "range"
    const [resultDate, setResultDate] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    // other filters
    const [buckets, setBuckets] = useState([]);
    const [unreadOnly, setUnreadOnly] = useState(() => {
        const saved = localStorage.getItem("screenerResults_unreadOnly");
        return saved === null ? true : saved === "true";
    });

    useEffect(() => {
        localStorage.setItem("screenerResults_unreadOnly", unreadOnly);
    }, [unreadOnly]);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {};

            // ✅ send only when the filter is complete
            if (dateMode === "single" && resultDate) {
                params.resultDate = resultDate;
            } else if (dateMode === "range") {
                // only send when BOTH are present
                if (dateFrom && dateTo) {
                    params.fromDate = dateFrom;
                    params.toDate = dateTo;
                } else if (!dateFrom && !dateTo) {
                    // neither set → that's allowed → no date filter
                } else {
                    // half-filled range → don't send date params at all
                }
            }

            if (unreadOnly) params.unreadOnly = true;

            const qs = new URLSearchParams(params).toString();
            const res = await fetch(`/api/screener-results${qs ? `?${qs}` : ""}`);
            const baseData = await res.json();

            // load holders
            const symbols = Array.from(new Set((baseData || []).map(r => r.symbol).filter(Boolean)));
            let holdersMap = {};
            if (symbols.length) {
                const resH = await fetch(`/api/screener-results/holders`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ symbols }),
                });
                const holders = await resH.json();
                holdersMap = holders || {};
            }

            const merged = (baseData || []).map(r => {
                const normalizedRead = r.isRead ?? r.read ?? false;
                return {
                    ...r,
                    read: normalizedRead,
                    isRead: normalizedRead,
                    superstarHolders: holdersMap[r.symbol] || [],
                };
            });

            setResults(merged);
        } catch (e) {
            setError(e?.message || "Failed to load screener results");
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    // ✅ smarter loader: don't call backend on half-filled range
    useEffect(() => {
        if (dateMode === "single") {
            // single-date mode: go ahead anytime it changes
            load();
            return;
        }

        // range mode
        if (!dateFrom && !dateTo) {
            // empty range → treat like "no date filter"
            load();
            return;
        }

        if (dateFrom && dateTo) {
            // range complete → load
            load();
            return;
        }

        // else: one side is missing → DO NOTHING (wait for user to finish)
    }, [dateMode, resultDate, dateFrom, dateTo, unreadOnly]);

    const markRead = async (id, read = true) => {
        try {
            const res = await fetch(`/api/screener-results/${id}/read`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ read }),
            });
            if (!res.ok) return false;
            setResults(prev => prev.map(r => (r.id === id ? { ...r, read, isRead: read } : r)));
            return true;
        } catch (e) {
            return false;
        }
    };

    const markReadBulk = async (ids, read = true) => {
        if (!ids.length) return true;
        try {
            const res = await fetch(`/api/screener-results/read`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ids, read }),
            });
            if (!res.ok) return false;
            setResults(prev =>
                prev.map(r => (ids.includes(r.id) ? { ...r, isRead: read, read } : r))
            );
            return true;
        } catch (e) {
            return false;
        }
    };

    const uniqueDates = useMemo(() => {
        const s = new Set(results.map(r => r.resultDate));
        return Array.from(s).sort().reverse();
    }, [results]);

    return (
        <ScreenerResultsDisplayContext.Provider
            value={{
                results,
                loading,
                error,
                filters: {
                    dateMode,
                    setDateMode,
                    resultDate,
                    setResultDate,
                    dateFrom,
                    setDateFrom,
                    dateTo,
                    setDateTo,
                    buckets,
                    setBuckets,
                    unreadOnly,
                    setUnreadOnly,
                },
                uniqueDates,
                reload: load,
                markRead,
                markReadBulk,
            }}
        >
            {children}
        </ScreenerResultsDisplayContext.Provider>
    );
};

export const useScreenerResultsDisplay = () =>
    useContext(ScreenerResultsDisplayContext);
